import { h as __h } from "preact";
import { Component, h } from "preact";
import { groupResponse } from "~/backend/validate.js";
import { ApiError, authedApiRequest, response } from "~/lib/api.js";
import { alert } from "~/util/alert.js";
import html from "~/util/html.js";
import { modal } from "~/util/modal.js";
import { PromiseSignal } from "~/util/promise.js";
import Query from "~/util/query.js";
import { object, parse, string } from "~/util/validate.js";
import Button from "~/component/Button.js";
import Field from "~/component/Field.js";
import adminStyle from "~/admin/lib/style.js";
import input from "~/style/input.js";

/** @typedef {(json: { name: string; description: string }) => Promise<groupResponse>} SubmitCallback */

/** @param {{ orgId: string }} props */
export const addGroup = async ({
  orgId
}) => {
  /** @type {SubmitCallback} */
  const onSubmit = async json => {
    const {
      data
    } = await authedApiRequest("POST", `/v2/orgs/${orgId}/groups`, {
      json,
      schema: response(groupResponse)
    });
    Query.invalidate([`/v2/orgs/${orgId}/groups`]);
    alert(`Created group '${data.name}'`, {
      type: "success"
    });
    return data;
  };
  return await modal({
    title: "Create group",
    content: (/** @type {(result?: groupResponse) => void} */resolve) => h(ModalChildren, {
      resolve,
      onSubmit
    })
  });
};

/**
 * @extends {Component<ModalChildrenProps>}
 *
 * @typedef {object} ModalChildrenProps
 * @property {(result?: groupResponse) => void} resolve
 * @property {SubmitCallback} onSubmit
 */
class ModalChildren extends Component {
  formState = new PromiseSignal(/** @param {SubmitEvent & { currentTarget: HTMLFormElement }} ev */
  async ev => {
    const form = new FormData(ev.currentTarget);
    const data = parse(object({
      name: string,
      description: string
    }), Object.fromEntries(form.entries()));
    this.props.resolve(await this.props.onSubmit(data));
  });
  render() {
    const error = this.formState.error && ApiError.from(this.formState.error);
    return __h("form", {
      "class": adminStyle.modal,
      onSubmit: (/** @type {SubmitEvent & { currentTarget: HTMLFormElement }} */ev) => {
        if (ev.submitter?.getAttribute("formmethod") === "dialog") return;
        ev.preventDefault();
        this.formState.invoke(ev);
      }
    }, __h(Field, {
      "for": "name",
      label: "Name",
      error: error?.fields.name?.message
    }, __h("input", {
      "class": input.input,
      id: "name",
      name: "name",
      required: true,
      autofocus: true
    })), __h(Field, {
      "for": "description",
      label: "Description (Optional)",
      error: error?.fields.description?.message
    }, __h("input", {
      "class": input.input,
      id: "description",
      name: "description"
    })), __h("p", {
      "class": adminStyle.error
    }, error?.message), __h("div", {
      "class": adminStyle.actions
    }, __h(Button, {
      type: "submit",
      level: "primary",
      loading: this.formState.loading
    }, "Create group"), __h(Button, {
      type: "submit",
      formmethod: "dialog",
      formnovalidate: true,
      value: "cancel"
    }, "Cancel")));
  }
}